import { PlayerProfileDoc } from '@/features/player/components/types';
import { fetchPlayerProfile } from '@/features/player/queries/get-player-profile';
import { playerKeys } from '@/features/player/queries/query-keys';
import { PlayerServices } from '@/features/player/services';
import { mapPlayersLookupResponse } from '@/features/player/utils/map-player-lookup';
import { ApiError, PaginatedResponse } from '@/libs/api';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

export function usePlayerProfileQuery(playerId?: string) {
  return useQuery({
    queryKey: playerKeys.profile(playerId),
    queryFn: () => fetchPlayerProfile(playerId as string),
    enabled: Boolean(playerId),
  });
}

export function useCoachPlayerProfileQuery(playerId?: string) {
  return useQuery({
    queryKey: playerKeys.profile(playerId),
    queryFn: () => PlayerServices.getCoachPlayer(playerId as string),
    enabled: Boolean(playerId),
  });
}

export const useListPlayersQuery = (
  request: player.SortFilterSearchPlayersRequest,
): UseQueryResult<PaginatedResponse<player.PlayerLookupData>, ApiError> => {
  return useQuery<
    player.PlayersLookupResponse,
    ApiError,
    PaginatedResponse<player.PlayerLookupData>
  >({
    queryKey: playerKeys.list(request),
    queryFn: () => PlayerServices.sortFilterSearchPlayers(request),
    enabled: !!request.academyId,
    select: data => {
      const rows = mapPlayersLookupResponse(data);
      const numberOfPages = Math.ceil(
        (rows[0]?.totalQueryResults ?? 0) / (request.resultsPerPage ?? 20),
      );
      return {
        data: rows,
        meta: {
          page: request.expectedPage ?? 1,
          limit: request.resultsPerPage ?? 20,
          total: rows[0]?.totalQueryResults ?? 0,
          totalPages: numberOfPages,
        },
      };
    },
    placeholderData: previousData => previousData,
  });
};

export const useCreatePlayerMutation = () => {
  return useMutation({
    mutationFn: (player: PlayerProfileDoc) => PlayerServices.createCoachPlayer(player),
  });
};

export const useRemovePlayerMutation = () => {
  return useMutation({
    mutationFn: (playerId: string) => PlayerServices.removeCoachPlayer(playerId),
  });
};

export const useUpdateCoachPlayerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ playerId, data }: { playerId: string; data: Partial<PlayerProfileDoc> }) =>
      PlayerServices.updateCoachPlayer(playerId, data),
    onSuccess: (updatedPlayer, { playerId }) => {
      queryClient.setQueryData(playerKeys.profile(playerId), updatedPlayer);
    },
  });
};
